import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";

// ---- Typen -----------------------------------------------------------------

export interface InsuranceProductDto {
  id: number;
  name: string;
  kuerzel?: string;
  detail?: string | null;
  isRelevantForPerson: boolean;
  isRelevantForCompany: boolean;
  allowsBundle: boolean;
  allowsCollective: boolean;
  sortOrder: number;
  active: boolean;
  insuranceClassID: number;
}

export interface InsuranceClassDto {
  id: number;
  insuranceClass: string;
  isClaimRelevant: boolean;
  sortOrder: number;
  active: boolean;
  products?: InsuranceProductDto[];
}

export type Kundenart = "FK" | "PK" | null;

export interface SparteMappingDto {
  id: number;
  rawVertragsart: string;
  kundenart?: Kundenart;
  insuranceProductID?: number | null;
  insuranceProduct?: InsuranceProductDto | null;
  active: boolean;
  sortOrder: number;
}

export interface OpenBranchDto {
  branch: string;
  count: number;
}

// ---- Mutate-Helfer (analog verantwortlichkeitService) ----------------------

async function mutate<T>(
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T | null> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `Fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (method === "DELETE") return null;
  return (await res.json()) as T;
}

// ---- Taxonomie -------------------------------------------------------------

/** Kategorien inkl. ihrer Vertragsarten (nur aktive, mit Produkten). */
export const getTaxonomy = async (): Promise<InsuranceClassDto[]> => {
  const { data } = await apiFetch<InsuranceClassDto[]>("insurance-class", {
    params: { withProducts: true },
  });
  return Array.isArray(data) ? data : [];
};

export const listClasses = async (): Promise<InsuranceClassDto[]> => {
  const { data } = await apiFetch<InsuranceClassDto[]>("insurance-class");
  return Array.isArray(data) ? data : [];
};

export const createClass = (input: Partial<InsuranceClassDto>) =>
  mutate<InsuranceClassDto>("insurance-class", "POST", input);
export const updateClass = (id: number, input: Partial<InsuranceClassDto>) =>
  mutate<InsuranceClassDto>(`insurance-class/${id}`, "PUT", input);
export const deleteClass = (id: number) =>
  mutate<void>(`insurance-class/${id}`, "DELETE");

export const createProduct = (input: Partial<InsuranceProductDto>) =>
  mutate<InsuranceProductDto>("insurance-product", "POST", input);
export const updateProduct = (id: number, input: Partial<InsuranceProductDto>) =>
  mutate<InsuranceProductDto>(`insurance-product/${id}`, "PUT", input);
export const deleteProduct = (id: number) =>
  mutate<{ disabled: boolean }>(`insurance-product/${id}`, "DELETE");

// ---- Mapping ---------------------------------------------------------------

export const listMapping = async (): Promise<SparteMappingDto[]> => {
  const { data } = await apiFetch<SparteMappingDto[]>("sparte-mapping");
  return Array.isArray(data) ? data : [];
};

export const createMapping = (input: Partial<SparteMappingDto>) =>
  mutate<SparteMappingDto>("sparte-mapping", "POST", input);
export const updateMapping = (id: number, input: Partial<SparteMappingDto>) =>
  mutate<SparteMappingDto>(`sparte-mapping/${id}`, "PUT", input);
export const deleteMapping = (id: number) =>
  mutate<void>(`sparte-mapping/${id}`, "DELETE");

export const getOpenBranches = async (): Promise<OpenBranchDto[]> => {
  const { data } = await apiFetch<OpenBranchDto[]>("sparte-mapping/open-branches");
  return Array.isArray(data) ? data : [];
};

export const applyMapping = (force = false) =>
  mutate<{ updated: number; mappingsApplied: number }>("sparte-mapping/apply", "POST", { force });
